import React from "react";
import { FaFacebook, FaInstagram, FaLinkedin, FaApple, FaGooglePlay } from "react-icons/fa";
import { ChevronRight, MapPin, Phone, Mail } from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";
import FooterMob from "./Footer_mob";


const CITIES = [
  { name: "Bangalore", slug: "bangalore" },
  { name: "Mysore", slug: "mysore" },
  { name: "Chennai", slug: "chennai" },
  { name: "Vellore", slug: "vellore" },
  { name: "Pondicherry", slug: "pondicherry" },
  { name: "Trichy", slug: "trichy" },
  { name: "Coimbatore", slug: "coimbatore" },
  { name: "Madurai", slug: "madurai" },
];

const QUICK_LINKS = [
  { name: "Home", path: "/" },
  { name: "FAQ's", path: "/faq" },
  { name: "Privacy Policy", path: "/privacypolicy" },
  { name: "Terms & Conditions", path: "/tandc" },
  { name: "Partner With Us", path: "https://partner-with-us.wowcarz.in/" },
];

const APP_STORE_URL = "https://apps.apple.com/in/app/wowcarz-self-drive-car-rental/id1613284063";
const PLAY_STORE_URL = "https://play.google.com/store/apps/details?id=com.rocketflyer.wowcarz";

const SOCIALS = [
  { name: "Facebook", url: "https://www.facebook.com/wowcarz" },
  { name: "Instagram", url: "https://www.instagram.com/wow_carz/?hl=en" },
  { name: "LinkedIn", url: "https://in.linkedin.com/company/wowcarz" },
];

const CONTACT = {
  address: "#1029, 17TH Main Road, 2nd Block, BTM 4th Stage, Bangalore, 560076",
  mapUrl: "https://maps.google.com/?q=1029, 17TH Main Road, 2nd Block, BTM 4th Stage, Bangalore, 560076",
  phone: "+91 9844300066",
  emails: ["support@wowcarz.in", "feedback@wowcarz.in", "franchise@wowcarz.in"],
};

// Mobile labels follow the Figma copy; "Partner With Us" opens externally
const MOBILE_QUICK_LINKS = QUICK_LINKS.map((link) => ({
  ...link,
  label: link.name === "FAQ's" ? "FAQs" : link.name,
  external: link.name === "Partner With Us",
}));

const Footer = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const handleQuickLink = (link) => {
    if (link.name === "Home" && location.pathname === "/") {
      window.scrollTo({ top: 0, behavior: "smooth" });
    } else {
      navigate(link.path);
    }
  };

  return (
    <>
    <FooterMob
      cities={CITIES}
      quickLinks={MOBILE_QUICK_LINKS}
      onCityClick={(city) => navigate(`/self-drive-cars-${city.slug}`)}
      onQuickLinkClick={handleQuickLink}
      appStoreUrl={APP_STORE_URL}
      playStoreUrl={PLAY_STORE_URL}
      socials={SOCIALS}
      contact={CONTACT}
    />
    <footer className="hidden md:block bg-white text-stone-950 border border-[#b4b4b4] rounded-t-[70px] overflow-hidden relative z-10 -mt-20">
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          opacity: 0.24,
          background:
            "linear-gradient(180deg, #E8E5E5 36.97%, #8B8B9C 75.55%)",
        }}
      />
      <div className="w-full px-6 md:px-12 pt-10 pb-6 relative z-10">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 text-center md:text-left">
          {/* --- LEFT: Logo, description, app badges --- */}
          <div className="md:col-span-4 flex flex-col items-center md:items-start">
            <img
              fetchpriority="high"
              src="/assets/logo.png"
              className="w-[180px] md:w-[200px] h-auto object-contain mb-4"
              alt="Wowcarz Logo"
            />
            <p className="text-sm text-zinc-500 leading-5 mb-5">
              We have been proudly delivering reliable Self Drive car rental
              services since August 2016. At Wowcarz, our mission is to make
              every travel plan seamless and stress-free.
            </p>
            <div className="flex flex-row gap-3">
              <a
                href="https://apps.apple.com/in/app/wowcarz-self-drive-car-rental/id1613284063"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2.5 bg-white text-stone-900 border-2 border-[#c1bdfd] rounded-[6px] px-4 py-2.5 hover:border-[#a09af5] transition-colors"
              >
                <FaApple className="text-xl shrink-0" />
                <div className="flex flex-col items-start leading-none">
                  <span className="text-[9px] text-zinc-400 uppercase tracking-wide font-medium">Download on the</span>
                  <span className="text-sm font-bold">App Store</span>
                </div>
              </a>
              <a
                href="https://play.google.com/store/apps/details?id=com.rocketflyer.wowcarz"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2.5 bg-white text-stone-900 border-2 border-[#c1bdfd] rounded-[6px] px-4 py-2.5 hover:border-[#a09af5] transition-colors"
              >
                <FaGooglePlay className="text-lg shrink-0" />
                <div className="flex flex-col items-start leading-none">
                  <span className="text-[9px] text-zinc-400 uppercase tracking-wide font-medium">Get it on</span>
                  <span className="text-sm font-bold">Google Play</span>
                </div>
              </a>
            </div>
          </div>

          {/* --- Available Cities --- */}
          <div className="md:col-span-3 flex flex-col items-center md:items-start">
            <p className="text-xs font-bold uppercase tracking-widest text-red-600 mb-3">
              Available Cities
            </p>
            <div className="flex flex-wrap justify-center md:justify-start gap-2">
              {CITIES.map((city) => (
                <button
                  key={city.slug}
                  onClick={() => navigate(`/self-drive-cars-${city.slug}`)}
                  className="px-3.5 py-2 rounded-full border border-violet-300 text-xs font-medium text-stone-900 hover:bg-violet-50 transition-colors cursor-pointer"
                >
                  {city.name}
                </button>
              ))}
            </div>
          </div>

          {/* --- Quick Links --- */}
          <div className="md:col-span-2 flex flex-col items-center md:items-start">
            <p className="text-xs font-bold uppercase tracking-widest text-red-600 mb-3">
              Quick Links
            </p>
            <div className="flex flex-col items-center md:items-start gap-2.5">
              {QUICK_LINKS.map((link) =>
                link.name === "Partner With Us" ? (
                  <a
                    key={link.name}
                    href={link.path}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-1 text-sm text-zinc-500 hover:text-red-600 transition-colors"
                  >
                    <ChevronRight size={14} className="text-red-600 shrink-0" />
                    {link.name}
                  </a>
                ) : (
                  <button
                    key={link.name}
                    onClick={() => handleQuickLink(link)}
                    className="flex items-center gap-1 text-sm text-zinc-500 hover:text-red-600 transition-colors"
                  >
                    <ChevronRight size={14} className="text-red-600 shrink-0" />
                    {link.name}
                  </button>
                ),
              )}
            </div>
          </div>

          {/* --- Get In Touch --- */}
          <div className="md:col-span-3 flex flex-col items-center md:items-start">
            <p className="text-xs font-bold uppercase tracking-widest text-red-600 mb-3">
              Get In Touch
            </p>
            <div className="flex flex-col items-center md:items-start gap-4">
              <a
                href="https://maps.google.com/?q=1029, 17TH Main Road, 2nd Block, BTM 4th Stage, Bangalore, 560076"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-start gap-3 hover:underline"
              >
                <span className="size-8 rounded-2xl border border-red-300 flex items-center justify-center shrink-0">
                  <MapPin size={14} className="text-neutral-700" />
                </span>
                <span className="flex flex-col items-start gap-0.5 text-left">
                  <span className="text-[11px] font-bold uppercase tracking-wide text-neutral-400">
                    Address
                  </span>
                  <span className="text-xs text-stone-950 leading-5 max-w-[220px]">
                    #1029, 17TH Main Road, 2nd Block, BTM 4th Stage, Bangalore,
                    560076
                  </span>
                </span>
              </a>

              <a
                href="tel:+919844300066"
                className="flex items-center gap-3 hover:underline"
              >
                <span className="size-8 rounded-2xl border border-red-300 flex items-center justify-center shrink-0">
                  <Phone size={14} className="text-neutral-700" />
                </span>
                <span className="flex flex-col items-start gap-0.5 text-left">
                  <span className="text-[11px] font-bold uppercase tracking-wide text-neutral-400">
                    Phone
                  </span>
                  <span className="text-xs text-stone-950">
                    +91 9844300066
                  </span>
                </span>
              </a>

              <div className="flex items-start gap-3">
                <span className="size-8 rounded-2xl border border-red-300 flex items-center justify-center shrink-0">
                  <Mail size={14} className="text-neutral-700" />
                </span>
                <span className="flex flex-col items-start gap-0.5 text-left">
                  <span className="text-[11px] font-bold uppercase tracking-wide text-neutral-400">
                    Email Addresses
                  </span>
                  {["support", "feedback", "franchise"].map((email) => (
                    <a
                      key={email}
                      href={`https://mail.google.com/mail/?view=cm&fs=1&to=${email}@wowcarz.in`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs text-stone-950 hover:underline"
                    >
                      {email}@wowcarz.in
                    </a>
                  ))}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* --- Skyline banner with watermark --- */}
        <div className="mt-10 -mx-6 md:-mx-12 overflow-hidden relative h-[160px] md:h-[260px]">
          {/* Layer 1 (back): watermark text */}
          <p
            className="absolute inset-x-0 top-0 flex items-start justify-center text-center select-none pointer-events-none whitespace-nowrap z-0"
            style={{
              fontSize: "clamp(80px, 14vw, 224px)",
              fontFamily: "'Outfit', sans-serif",
              fontWeight: 900,
              letterSpacing: "32.83px",
              lineHeight: 1,
              color: "#DEDEE1",
              textShadow: "1px 1px 2px rgba(109,93,93,0.50)",
            }}
            aria-hidden="true"
          >WOWCARZ</p>

          {/* Layer 2 (front): cityscape fades in from top so watermark shows through */}
          <img
            src="/assets/cityscape_footer.png"
            alt="Wowcarz cities"
            className="absolute inset-0 w-full h-full object-cover object-bottom z-10"
            style={{
              maskImage: "linear-gradient(to bottom, transparent 0%, black 45%)",
              WebkitMaskImage: "linear-gradient(to bottom, transparent 0%, black 45%)",
            }}
          />

        </div>

        {/* --- Bottom bar --- */}
        <div className="-mx-6 md:-mx-12 -mb-6 md:-mb-6 px-10 py-4 flex items-center gap-4 bg-white relative z-10">
          <div className="flex items-center gap-6">
            <a target="_blank" rel="noopener noreferrer" href="https://www.facebook.com/wowcarz" className="size-9 shrink-0">
              <img src="/assets/footer/social-facebook.svg" alt="Facebook" className="size-full" />
            </a>
            <a target="_blank" rel="noopener noreferrer" href="https://www.instagram.com/wow_carz/?hl=en" className="size-9 shrink-0">
              <img src="/assets/footer/social-instagram.svg" alt="Instagram" className="size-full" />
            </a>
            <a target="_blank" rel="noopener noreferrer" href="https://in.linkedin.com/company/wowcarz" className="size-9 shrink-0">
              <img src="/assets/footer/social-linkedin.svg" alt="LinkedIn" className="size-full" />
            </a>
          </div>
          <span className="font-medium font-['Outfit'] text-[16px] text-[#808080] whitespace-nowrap">
            Copyright, Wow Carz {new Date().getFullYear()}. All rights reserved.
          </span>
        </div>
      </div>
    </footer>
    </>
  );
};

export default Footer;